/**
 * Visibility rules for offers.
 *
 * Kept free of Payload and Next imports so both the listing and the detail
 * page share one definition of "live", and so the rules can be unit tested
 * without a database.
 *
 * Dates are evaluated at read time rather than written back onto the doc by a
 * scheduled job: an offer that expires simply stops being live, and editing
 * its expiration date brings it straight back.
 */

/** The subset of an event offer these rules need. */
export interface OfferWindow {
  status?: ('active' | 'draft' | 'archived') | null
  startDate?: string | null
  expirationDate?: string | null
}

/**
 * Listing visibility, which is separate from whether an offer is live.
 *
 * An offer promoted on a partner's site is claimable like any other — its own
 * page and the consultation form honour it — it is only kept off our /offers
 * list so the same offer is not advertised twice.
 */
export interface OfferListing {
  hideFromOffersPage?: boolean | null
}

/** An offer carrying its `events` relationship, populated or not. */
export interface OfferWithEvent {
  event?: number | { title?: string | null } | null
}

/** The offer's own headline, which labels it when there is no event. */
export interface OfferTitled {
  offerTitle?: string | null
}

export type OfferAvailability = 'live' | 'scheduled' | 'expired' | 'unavailable'

function toTime(value: string | null | undefined): number | null {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Where an offer sits relative to its window.
 *
 * - `unavailable` — not Active in the CMS, so it never reaches the public site.
 * - `scheduled` — Active, but its start date has not arrived yet.
 * - `expired` — Active, but past its expiration date.
 * - `live` — Active and inside its window.
 *
 * An unparseable or missing date is treated as "no bound", which keeps a
 * half-filled doc visible rather than silently hiding it.
 */
export function getOfferAvailability(
  offer: OfferWindow,
  now: Date = new Date(),
): OfferAvailability {
  if (offer.status !== 'active') return 'unavailable'

  const current = now.getTime()
  const start = toTime(offer.startDate)
  const end = toTime(offer.expirationDate)

  if (start !== null && current < start) return 'scheduled'
  // The expiration date is inclusive of the instant itself; an offer expires once
  // the clock passes it.
  if (end !== null && current > end) return 'expired'

  return 'live'
}

/** True when the offer can be claimed: inside its window and Active. */
export function isOfferLive(offer: OfferWindow, now: Date = new Date()): boolean {
  return getOfferAvailability(offer, now) === 'live'
}

/**
 * True when a live offer also belongs on the /offers list.
 *
 * Deliberately not folded into `isOfferLive`: hiding an offer from the list
 * must not stop it being applied to a consultation, which is the whole point
 * of the checkbox.
 */
export function isOfferListed(offer: OfferListing): boolean {
  return offer.hideFromOffersPage !== true
}

/**
 * The event an offer points at, once Payload has populated the relationship.
 *
 * An offer queried at depth 0 carries the event's id instead of the document,
 * and a relationship can dangle if the event is deleted, so every read goes
 * through here rather than assuming the object is there.
 */
export function resolveOfferEvent<E extends { title?: string | null }>(
  event: number | E | null | undefined,
): E | null {
  return event && typeof event === 'object' ? event : null
}

/** The related event's name, when an offer has one. */
export function offerEventName(
  event: number | { title?: string | null } | null | undefined,
): string | null {
  return resolveOfferEvent(event)?.title?.trim() || null
}

/**
 * What an offer is called on /offers.
 *
 * An event offer is labelled by its event: someone scanning a QR code knows
 * which event they attended, and recognising it is the whole job of the card.
 * A standalone offer has no event to recognise, so it falls back to its own
 * title — which is also what an offer whose event was deleted ends up showing,
 * rather than vanishing from the list while still being claimable elsewhere.
 */
export function offerCardLabel(offer: OfferWithEvent & OfferTitled): string | null {
  return offerEventName(offer.event) || offer.offerTitle?.trim() || null
}

/**
 * The offers the /offers list shows, sorted by label so it reads
 * alphabetically — event offers under their event's name, standalone ones
 * under their own title, in one list.
 *
 * An offer with no label at all is dropped: there is nothing to put on a card.
 * So is one marked "hide from offers page" — it stays live everywhere else.
 */
export function selectLiveOffers<
  T extends OfferWindow & OfferWithEvent & OfferListing & OfferTitled,
>(offers: T[], now: Date = new Date()): T[] {
  return offers
    .filter((offer) => isOfferLive(offer, now) && isOfferListed(offer) && offerCardLabel(offer))
    .sort((a, b) => (offerCardLabel(a) || '').localeCompare(offerCardLabel(b) || ''))
}

/** Query parameter carrying an offer into the consultation form. */
export const OFFER_PARAM = 'offer'

/**
 * Where an offer card sends someone: straight into the consultation form, with
 * the offer named so the form can show what is being applied and submit it with
 * the lead. The offer detail page is not part of this path.
 */
export function consultationHrefForOffer(slug: string): string {
  return `/consultation?${OFFER_PARAM}=${encodeURIComponent(slug)}`
}

/**
 * Internal paths stay relative; anything else must be an absolute http(s) URL.
 * Guards against a CMS value like `javascript:…` reaching an anchor's href.
 */
export function normalizeCtaLink(link: string | null | undefined): string | null {
  const trimmed = (link || '').trim()
  if (!trimmed) return null
  if (trimmed.startsWith('/')) return trimmed
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return null
}

/**
 * Where an offer's own detail page should send someone.
 *
 * A destination the team has pointed somewhere specific — a Calendly link for
 * one event, say — is honoured as it stands. Anything left at the consultation
 * form instead carries the offer with it, so arriving through a QR code aimed
 * at a single offer applies it exactly as the cards on /offers do.
 */
export function offerDetailCtaHref(slug: string, ctaLink: string | null | undefined): string {
  const normalized = normalizeCtaLink(ctaLink)
  if (!normalized) return consultationHrefForOffer(slug)

  const [path] = normalized.split('?')
  if (path === '/consultation') return consultationHrefForOffer(slug)

  return normalized
}

/**
 * The scheduling link shown after the consultation form is submitted with an
 * offer applied. Only absolute https URLs are honoured; anything else is
 * treated as unset so the visitor falls back to the team following up.
 */
export function normalizeBookingLink(link: string | null | undefined): string | null {
  const trimmed = (link || '').trim()
  return /^https:\/\//i.test(trimmed) ? trimmed : null
}

/** True for links that leave the site, which get `target="_blank"` treatment. */
export function isExternalLink(link: string): boolean {
  return /^https?:\/\//i.test(link)
}

/** e.g. "March 14, 2026" — used for the "offer ends" line. */
export function formatOfferDate(value: string | null | undefined): string | null {
  const time = toTime(value)
  if (time === null) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(time))
}
